import { supabase } from '../lib/supabase';
import JSZip from 'jszip';

class MusicalService {
  // Get all musicals
  async getMusicals(searchTerm = '') {
    try {
      let query = supabase?.from('musicals')?.select(`
          id,
          title,
          year_premiered,
          composer,
          lyricist,
          book_writer,
          published_by,
          rights_group,
          poster_url,
          description,
          genre,
          is_active,
          is_featured,
          created_at,
          updated_at,
          created_by,
          updated_by
        `)?.eq('is_active', true);

      if (searchTerm?.trim()) {
        query = query?.or(`title.ilike.%${searchTerm}%,composer.ilike.%${searchTerm}%,lyricist.ilike.%${searchTerm}%`);
      }

      query = query?.order('title', { ascending: true });

      const { data, error } = await query;
      
      // Generate poster URLs for musicals that don't have them stored
      if (data && !error) {
        // Fetch asset counts per musical in a single batch query
        let assetCountsMap = {};
        try {
          const musicalIds = data?.map(m => m?.id);
          if (musicalIds?.length > 0) {
            // Query songs with their asset counts for all musicals at once
            const { data: songsData } = await supabase
              ?.from('songs')
              ?.select(`
                id,
                musical_id,
                midi_file_path,
                mp3_file_path,
                song_videos (id),
                character_song_analyses (id)
              `)
              ?.in('musical_id', musicalIds);

            if (songsData) {
              songsData?.forEach(song => {
                const mid = song?.musical_id;
                if (!assetCountsMap?.[mid]) {
                  assetCountsMap[mid] = { videos_count: 0, midi_count: 0, mp3_count: 0, lessons_count: 0 };
                }
                assetCountsMap[mid].videos_count += song?.song_videos?.length || 0;
                assetCountsMap[mid].midi_count += song?.midi_file_path ? 1 : 0;
                assetCountsMap[mid].mp3_count += song?.mp3_file_path ? 1 : 0;
                assetCountsMap[mid].lessons_count += song?.character_song_analyses?.length || 0;
              });
            }
          }
        } catch {
          // silently ignore asset count errors
        }

        const musicalsWithPosters = await Promise.all(
          data?.map(async (musical) => {
            let posterUrl = musical?.poster_url;
            
            // If no poster_url is stored, try to generate one from storage
            if (!posterUrl) {
              try {
                posterUrl = await this.generatePosterUrl(musical?.id);
              } catch {
                posterUrl = null;
              }
            }
            
            const counts = assetCountsMap?.[musical?.id] || { videos_count: 0, midi_count: 0, mp3_count: 0, lessons_count: 0 };

            return {
              ...musical,
              poster_url: posterUrl,
              ...counts
            };
          })
        );
        
        return { data: musicalsWithPosters, error };
      }
      
      return { data, error };
    } catch (err) {
      return { data: null, error: err };
    }
  }

  // Get musical by ID with related data
  async getMusicalById(id) {
    try {
      const { data, error } = await supabase?.from('musicals')?.select(`
          *,
          roles (
            id,
            character_name,
            role_type,
            voice_type,
            description
          ),
          songs (
            id,
            title,
            act_number,
            song_order,
            song_roles (
              id,
              role_id,
              is_solo,
              vocal_part,
              roles (
                id,
                character_name,
                role_type,
                voice_type
              )
            )
          )
        `)?.eq('id', id)?.single();

      if (error) {
        return { data: null, error };
      }

      if (data) {
        let posterUrl = data?.poster_url;
        
        // If no poster_url is stored, try to generate one from storage
        if (!posterUrl) {
          try {
            posterUrl = await this.generatePosterUrl(data?.id);
          } catch {
            posterUrl = null;
          }
        }
        
        return {
          data: {
            ...data,
            poster_url: posterUrl
          },
          error: null
        };
      }

      return { data, error };
    } catch (err) {
      return { data: null, error: err };
    }
  }

  // Create new musical
  async createMusical(musicalData) {
    try {
      // Process year_premiered to ensure proper type conversion
      const processedYear = musicalData?.year_premiered === '' || musicalData?.year_premiered === undefined 
        ? null 
        : musicalData?.year_premiered ? parseInt(musicalData?.year_premiered, 10) : null;

      const { data, error } = await supabase?.from('musicals')?.insert([{
          title: musicalData?.title,
          composer: musicalData?.composer,
          lyricist: musicalData?.lyricist,
          book_writer: musicalData?.book_writer,
          year_premiered: processedYear,
          genre: musicalData?.genre,
          description: musicalData?.description,
          published_by: musicalData?.published_by,
          rights_group: musicalData?.rights_group,
          songs: musicalData?.songs || [],
          characters: musicalData?.characters || [],
          is_active: musicalData?.is_active !== false,
          poster_url: musicalData?.poster_url,
          created_by: musicalData?.created_by
        }])?.select()?.single();

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error creating musical:', error);
      return { data: null, error };
    }
  }

  // Update musical
  async updateMusical(id, musicalData) {
    try {
      const updateData = {};
      
      // Only include fields that are provided
      if (musicalData?.title !== undefined) updateData.title = musicalData?.title;
      if (musicalData?.composer !== undefined) updateData.composer = musicalData?.composer;
      if (musicalData?.lyricist !== undefined) updateData.lyricist = musicalData?.lyricist;
      if (musicalData?.book_writer !== undefined) updateData.book_writer = musicalData?.book_writer;
      
      // Special handling for year_premiered to prevent empty string errors
      if (musicalData?.year_premiered !== undefined) {
        const processedYear = musicalData?.year_premiered === '' || musicalData?.year_premiered === undefined 
          ? null 
          : musicalData?.year_premiered ? parseInt(musicalData?.year_premiered, 10) : null;
        updateData.year_premiered = processedYear;
      }
      
      if (musicalData?.genre !== undefined) updateData.genre = musicalData?.genre;
      if (musicalData?.description !== undefined) updateData.description = musicalData?.description;
      if (musicalData?.published_by !== undefined) updateData.published_by = musicalData?.published_by;
      if (musicalData?.rights_group !== undefined) updateData.rights_group = musicalData?.rights_group;
      if (musicalData?.songs !== undefined) updateData.songs = musicalData?.songs;
      if (musicalData?.characters !== undefined) updateData.characters = musicalData?.characters;
      if (musicalData?.poster_url !== undefined) updateData.poster_url = musicalData?.poster_url;
      if (musicalData?.is_active !== undefined) updateData.is_active = musicalData?.is_active;
      if (musicalData?.is_featured !== undefined) updateData.is_featured = musicalData?.is_featured;

      // Always update the updated_by and updated_at fields
      updateData.updated_by = musicalData?.updated_by;
      updateData.updated_at = new Date()?.toISOString();

      const { data, error } = await supabase?.from('musicals')?.update(updateData)?.eq('id', id)?.select()?.single();

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error updating musical:', error);
      return { data: null, error };
    }
  }

  // Delete musical
  async deleteMusical(id) {
    try {
      const { data, error } = await supabase?.from('musicals')?.delete()?.eq('id', id);

      return { data, error };
    } catch (err) {
      return { data: null, error: err };
    }
  }

  // Create character/role for musical
  async createCharacter(characterData) {
    try {
      const { data, error } = await supabase?.from('roles')?.insert([{
          musical_id: characterData?.musical_id,
          character_name: characterData?.character_name,
          role_type: characterData?.role_type || 'ensemble',
          voice_type: characterData?.voice_type || 'speaking',
          description: characterData?.description || null,
          created_by: characterData?.created_by
        }])?.select()?.single();

      return { data, error };
    } catch (err) {
      return { data: null, error: err };
    }
  }

  // Get characters for musical
  async getCharactersByMusicalId(musicalId) {
    try {
      const { data, error } = await supabase?.from('roles')?.select('*')?.eq('musical_id', musicalId)?.order('character_name', { ascending: true });

      return { data, error };
    } catch (err) {
      return { data: null, error: err };
    }
  }

  // Delete character
  async deleteCharacter(id) {
    try {
      const { data, error } = await supabase?.from('roles')?.delete()?.eq('id', id);

      return { data, error };
    } catch (err) {
      return { data: null, error: err };
    }
  }

  // Create song for musical
  async createSong(songData) {
    try {
      const { data, error } = await supabase?.from('songs')?.insert([{
          musical_id: songData?.musical_id,
          title: songData?.title,
          created_by: songData?.created_by,
          act_number: null,
          song_order: null,
          tempo: null,
          key_signature: null,
          time_signature: '4/4',
          lyrics: null,
          audio_url: null,
          duration_seconds: null,
          is_featured: false
        }])?.select()?.single();

      return { data, error };
    } catch (err) {
      return { data: null, error: err };
    }
  }

  // Get songs for musical
  async getSongsByMusicalId(musicalId) {
    try {
      const { data, error } = await supabase?.from('songs')?.select('*')?.eq('musical_id', musicalId)?.order('song_order', { ascending: true, nullsFirst: true });

      return { data, error };
    } catch (err) {
      return { data: null, error: err };
    }
  }

  // Delete song
  async deleteSong(id) {
    try {
      const { data, error } = await supabase?.from('songs')?.delete()?.eq('id', id);

      return { data, error };
    } catch (err) {
      return { data: null, error: err };
    }
  }

  // Upload poster image
  async uploadPoster(file, musicalId) {
    try {
      const fileExt = file?.name?.split('.')?.pop()?.toLowerCase();
      const fileName = `${musicalId}/poster.${fileExt}`;
      
      // First try to remove existing file with same musical ID (different extensions)
      const extensions = ['jpg', 'jpeg', 'png', 'webp', 'gif'];
      for (const ext of extensions) {
        const existingFileName = `${musicalId}/poster.${ext}`;
        try {
          await supabase?.storage?.from('musical-posters')?.remove([existingFileName]);
        } catch (err) {
          // Ignore errors when removing non-existent files
          console.warn(`Could not remove existing poster: ${existingFileName}`);
        }
      }
      
      const { data: uploadData, error: uploadError } = await supabase?.storage?.from('musical-posters')?.upload(fileName, file, { 
          upsert: true,
          contentType: file?.type,
          cacheControl: '3600'
        });

      if (uploadError) {
        console.error('Upload error:', uploadError);
        return { data: null, error: uploadError };
      }

      // Get public URL with timestamp to avoid caching issues
      const { data: publicUrlData } = supabase?.storage?.from('musical-posters')?.getPublicUrl(fileName);
      
      // Add timestamp to prevent caching issues
      const timestampedUrl = `${publicUrlData?.publicUrl}?t=${Date.now()}`;

      return { 
        data: { 
          path: uploadData?.path, 
          publicUrl: timestampedUrl,
          originalUrl: publicUrlData?.publicUrl
        }, 
        error: null 
      };
    } catch (err) {
      console.error('Upload service error:', err);
      return { data: null, error: err };
    }
  }

  // Delete poster image
  async deletePoster(path) {
    try {
      const { data, error } = await supabase?.storage?.from('musical-posters')?.remove([path]);

      return { data, error };
    } catch (err) {
      return { data: null, error: err };
    }
  }

  // Bulk import posters from ZIP file
  async importPostersFromZip(zipFile, previewImages, userId) {
    try {
      const zip = new JSZip();
      const contents = await zip?.loadAsync(zipFile);
      
      const results = {
        imported: 0,
        skipped: 0,
        errors: []
      };

      // Process only matched images
      const matchedImages = previewImages?.filter(img => img?.matchingMusical);
      
      for (const imageInfo of matchedImages) {
        try {
          const zipEntry = contents?.files?.[imageInfo?.filename];
          if (!zipEntry) {
            results?.errors?.push(`File ${imageInfo?.filename} not found in ZIP`);
            continue;
          }

          // Get image data as blob
          const imageBlob = await zipEntry?.async('blob');
          
          // Create a proper File object for upload
          const imageFile = new File([imageBlob], imageInfo?.filename, {
            type: 'image/jpeg'
          });

          // Upload to storage
          const { data: uploadData, error: uploadError } = await this.uploadPoster(
            imageFile, 
            imageInfo?.matchingMusical?.id
          );

          if (uploadError) {
            results?.errors?.push(`${imageInfo?.matchingMusical?.title}: ${uploadError?.message}`);
            results.skipped++;
            continue;
          }

          // Update musical record with poster URL
          const { error: updateError } = await this.updateMusical(
            imageInfo?.matchingMusical?.id,
            {
              poster_url: uploadData?.publicUrl,
              updated_by: userId
            }
          );

          if (updateError) {
            results?.errors?.push(`${imageInfo?.matchingMusical?.title}: Failed to update database - ${updateError?.message}`);
            results.skipped++;
            continue;
          }

          results.imported++;
          
        } catch (err) {
          results?.errors?.push(`${imageInfo?.matchingMusical?.title}: ${err?.message}`);
          results.skipped++;
        }
      }

      return { data: results, error: null };
      
    } catch (err) {
      return { 
        data: null, 
        error: `Failed to process ZIP file: ${err?.message}` 
      };
    }
  }

  // Generate poster URL from storage if file exists
  async generatePosterUrl(musicalId) {
    try {
      if (!musicalId) return null;
      
      // Common image file extensions to check
      const extensions = ['jpg', 'jpeg', 'png', 'webp', 'gif'];
      
      for (const ext of extensions) {
        const fileName = `${musicalId}/poster.${ext}`;
        
        // Check if file exists in storage
        const { data: fileData, error: fileError } = await supabase?.storage
          ?.from('musical-posters')
          ?.list(musicalId, {
            limit: 10,
            search: `poster.${ext}`
          });
          
        if (!fileError && fileData?.length > 0) {
          // File exists, generate public URL
          const { data: publicUrlData } = supabase?.storage
            ?.from('musical-posters')
            ?.getPublicUrl(fileName);
            
          // Add timestamp to prevent caching issues
          const timestampedUrl = `${publicUrlData?.publicUrl}?t=${Date.now()}`;
          return timestampedUrl || null;
        }
      }
      
      return null;
    } catch (err) {
      console.warn('Error generating poster URL for musical:', musicalId, err);
      return null;
    }
  }

  // Get song count for musical
  async getSongCountForMusical(musicalId) {
    try {
      const { count, error } = await supabase?.from('songs')?.select('id', { count: 'exact' })?.eq('musical_id', musicalId);

      return { data: count, error };
    } catch (err) {
      return { data: 0, error: err };
    }
  }

  // Get role count for musical
  async getRoleCountForMusical(musicalId) {
    try {
      const { count, error } = await supabase?.from('roles')?.select('id', { count: 'exact' })?.eq('musical_id', musicalId);

      return { data: count, error };
    } catch (err) {
      return { data: 0, error: err };
    }
  }

  // Get analysis count for a musical
  async getAnalysisCountForMusical(musicalId) {
    try {
      // Get all roles for this musical
      const { data: roles, error: rolesError } = await supabase?.from('roles')?.select('id')?.eq('musical_id', musicalId);

      if (rolesError) {
        console.error('Error fetching roles:', rolesError);
        return { data: 0, error: rolesError };
      }

      if (!roles || roles?.length === 0) {
        return { data: 0, error: null };
      }

      // Get count of character_song_analyses for these roles
      const roleIds = roles?.map(role => role?.id);
      
      const { count, error: analysisError } = await supabase?.from('character_song_analyses')?.select('id', { count: 'exact', head: true })?.in('role_id', roleIds);

      if (analysisError) {
        console.error('Error counting analyses:', analysisError);
        return { data: 0, error: analysisError };
      }

      return { data: count || 0, error: null };
    } catch (err) {
      console.error('Error in getAnalysisCountForMusical:', err);
      return { data: 0, error: err };
    }
  }

  // Create multiple songs from CSV data
  async createSongsFromCSV(musicalId, songNames, createdBy) {
    if (!songNames?.length || !musicalId) return { data: [], error: null };
    
    try {
      const songsToCreate = songNames?.map((songName, index) => ({
        musical_id: musicalId,
        title: songName?.trim(),
        created_by: createdBy,
        song_order: index + 1,
        act_number: 1, // Default to Act 1
        tempo: null,
        key_signature: null,
        time_signature: '4/4',
        lyrics: null,
        audio_url: null,
        duration_seconds: null,
        is_featured: false
      }))?.filter(song => song?.title);

      if (songsToCreate?.length === 0) {
        return { data: [], error: null };
      }

      const { data, error } = await supabase
        ?.from('songs')
        ?.insert(songsToCreate)
        ?.select();

      return { data: data || [], error };
    } catch (err) {
      return { data: [], error: err };
    }
  }

  // Create multiple characters from CSV data  
  async createCharactersFromCSV(musicalId, characterNames, createdBy) {
    if (!characterNames?.length || !musicalId) return { data: [], error: null };
    
    try {
      const charactersToCreate = characterNames?.map(characterName => ({
        musical_id: musicalId,
        character_name: characterName?.trim(),
        role_type: 'ensemble', // Default role type
        voice_type: 'speaking', // Default voice type
        description: `Character from ${characterName?.trim()}`,
        created_by: createdBy,
        is_principal: false,
        age_range: null,
        vocal_range: null
      }))?.filter(character => character?.character_name);

      if (charactersToCreate?.length === 0) {
        return { data: [], error: null };
      }

      const { data, error } = await supabase
        ?.from('roles')
        ?.insert(charactersToCreate)
        ?.select();

      return { data: data || [], error };
    } catch (err) {
      return { data: [], error: err };
    }
  }

  // Create musical with songs and characters from CSV import
  async createMusicalWithRelatedData(musicalData) {
    try {
      // Extract songs and characters arrays from musicalData
      const songsArray = musicalData?.songs || [];
      const charactersArray = musicalData?.characters || [];
      
      // Create the musical first (without songs/characters in JSONB fields)
      const musicalToCreate = { ...musicalData };
      delete musicalToCreate?.songs;
      delete musicalToCreate?.characters;

      const { data: newMusical, error: createError } = await this.createMusical(musicalToCreate);
      
      if (createError || !newMusical) {
        throw createError || new Error('Failed to create musical');
      }

      // Create songs if provided
      let createdSongs = [];
      if (songsArray?.length > 0) {
        const songNames = songsArray?.map(song => 
          typeof song === 'string' ? song : song?.title || song?.name
        )?.filter(Boolean);
        
        if (songNames?.length > 0) {
          const { data: songsData, error: songsError } = await this.createSongsFromCSV(
            newMusical?.id, 
            songNames, 
            musicalData?.created_by
          );
          
          if (!songsError && songsData) {
            createdSongs = songsData;
          }
        }
      }

      // Create characters if provided  
      let createdCharacters = [];
      if (charactersArray?.length > 0) {
        const characterNames = charactersArray?.map(character =>
          typeof character === 'string' ? character : character?.name || character?.character_name
        )?.filter(Boolean);
        
        if (characterNames?.length > 0) {
          const { data: charactersData, error: charactersError } = await this.createCharactersFromCSV(
            newMusical?.id,
            characterNames,
            musicalData?.created_by
          );
          
          if (!charactersError && charactersData) {
            createdCharacters = charactersData;
          }
        }
      }

      // Return the created musical with summary of related data
      return {
        data: {
          ...newMusical,
          created_songs_count: createdSongs?.length || 0,
          created_characters_count: createdCharacters?.length || 0,
          created_songs: createdSongs,
          created_characters: createdCharacters
        },
        error: null
      };

    } catch (error) {
      return { data: null, error };
    }
  }

  // Find duplicate musicals based on title similarity
  async findDuplicateMusicals(title, similarityThreshold = 0.8) {
    try {
      const { data, error } = await supabase?.rpc('find_duplicate_musicals', {
        input_title: title,
        similarity_threshold: similarityThreshold
      });

      return { data: data || [], error };
    } catch (err) {
      return { data: [], error: err };
    }
  }

  // Compare two musicals for detailed information
  async compareMusicals(musical1Id, musical2Id) {
    try {
      const { data, error } = await supabase?.rpc('compare_musicals', {
        musical1_id: musical1Id,
        musical2_id: musical2Id
      });

      return { data: data || {}, error };
    } catch (err) {
      return { data: {}, error: err };
    }
  }

  // Merge musical data from source to target
  async mergeMusicalData(sourceMusicalId, targetMusicalId, options = {}) {
    try {
      const { data, error } = await supabase?.rpc('merge_musical_data', {
        source_musical_id: sourceMusicalId,
        target_musical_id: targetMusicalId,
        merge_songs: options?.mergeSongs !== false,
        merge_characters: options?.mergeCharacters !== false,
        updated_by_user: options?.updatedBy || null
      });

      return { data: data || {}, error };
    } catch (err) {
      return { data: {}, error: err };
    }
  }

  // Enhanced create musical with duplicate detection
  async createMusicalWithDuplicateDetection(musicalData, options = {}) {
    try {
      const { detectDuplicates = true, similarityThreshold = 0.8 } = options;
      
      // Check for duplicates if enabled
      let duplicates = [];
      if (detectDuplicates && musicalData?.title?.trim()) {
        const { data: duplicateData, error: duplicateError } = await this.findDuplicateMusicals(
          musicalData?.title?.trim(), 
          similarityThreshold
        );
        
        if (!duplicateError && duplicateData?.length > 0) {
          duplicates = duplicateData;
        }
      }

      // If duplicates found, return them for user decision
      if (duplicates?.length > 0) {
        return {
          data: null,
          error: null,
          duplicatesFound: true,
          duplicates,
          originalData: musicalData
        };
      }

      // No duplicates, proceed with normal creation
      return await this.createMusicalWithRelatedData(musicalData);
    } catch (error) {
      return { data: null, error };
    }
  }

  // Enhanced CSV import with duplicate detection and merging
  async importMusicalFromCSVWithDuplicateHandling(csvRowData, options = {}) {
    try {
      const { 
        detectDuplicates = true, 
        similarityThreshold = 0.8,
        autoMerge = false,
        createdBy 
      } = options;

      // Process CSV row data
      const processedData = {
        title: csvRowData?.title?.trim(),
        composer: csvRowData?.composer?.trim() || null,
        lyricist: csvRowData?.lyricist?.trim() || null,
        book_writer: csvRowData?.book_writer?.trim() || null,
        year_premiered: csvRowData?.year_premiered || null,
        genre: csvRowData?.genre?.toLowerCase() || 'broadway',
        description: csvRowData?.description?.trim() || null,
        published_by: csvRowData?.published_by?.trim() || null,
        rights_group: csvRowData?.rights_group?.trim() || null,
        songs: csvRowData?.songs || [],
        characters: csvRowData?.characters || [],
        is_active: true,
        created_by: createdBy
      };

      if (!processedData?.title) {
        return { 
          data: null, 
          error: 'Missing required title field',
          duplicatesFound: false 
        };
      }

      // Check for duplicates
      let duplicates = [];
      if (detectDuplicates) {
        const { data: duplicateData, error: duplicateError } = await this.findDuplicateMusicals(
          processedData?.title, 
          similarityThreshold
        );
        
        if (!duplicateError && duplicateData?.length > 0) {
          duplicates = duplicateData;
        }
      }

      // Handle duplicates found
      if (duplicates?.length > 0) {
        if (autoMerge) {
          // Auto-merge with the most similar (first) duplicate
          const targetMusical = duplicates?.[0];
          
          // Get additional data to merge
          const additionalSongs = processedData?.songs?.filter(song => {
            let songTitle = typeof song === 'string' ? song : song?.title || song?.name;
            return songTitle?.trim();
          }) || [];

          const additionalCharacters = processedData?.characters?.filter(character => {
            const characterName = typeof character === 'string' ? character : character?.name || character?.character_name;
            return characterName?.trim();
          }) || [];

          // Create temporary musical to merge data from
          let tempMusicalId = null;
          if (additionalSongs?.length > 0 || additionalCharacters?.length > 0) {
            const { data: tempMusical } = await this.createMusicalWithRelatedData(processedData);
            if (tempMusical) {
              tempMusicalId = tempMusical?.id;
              
              // Merge the data
              const { data: mergeResult } = await this.mergeMusicalData(
                tempMusicalId,
                targetMusical?.id,
                {
                  mergeSongs: additionalSongs?.length > 0,
                  mergeCharacters: additionalCharacters?.length > 0,
                  updatedBy: createdBy
                }
              );
              
              return {
                data: {
                  ...targetMusical,
                  merged: true,
                  merged_songs_count: mergeResult?.merged_songs || 0,
                  merged_characters_count: mergeResult?.merged_characters || 0
                },
                error: null,
                duplicatesFound: false
              };
            }
          }

          // No additional data to merge, just return existing musical
          return {
            data: {
              ...targetMusical,
              skipped: true,
              reason: 'Duplicate found, no additional data to merge'
            },
            error: null,
            duplicatesFound: false
          };
        } else {
          // Return duplicates for manual handling
          return {
            data: null,
            error: null,
            duplicatesFound: true,
            duplicates,
            originalData: processedData
          };
        }
      }

      // No duplicates, create new musical
      return await this.createMusicalWithRelatedData(processedData);
    } catch (error) {
      return { 
        data: null, 
        error: error?.message || 'Import failed',
        duplicatesFound: false 
      };
    }
  }

  // Search all musicals and create song entries from the songs JSONB field
  async syncMusicalSongsToSongsTable(createdBy = null) {
    try {
      const results = {
        processedMusicals: 0,
        songsCreated: 0,
        songsSkipped: 0,
        errors: []
      };

      // Get all active musicals with their songs JSONB data
      const { data: musicals, error: musicalsError } = await supabase
        ?.from('musicals')
        ?.select('id, title, songs')
        ?.eq('is_active', true)
        ?.not('songs', 'is', null);

      if (musicalsError) {
        return { data: null, error: musicalsError };
      }

      if (!musicals?.length) {
        return { 
          data: { 
            ...results, 
            message: 'No musicals found with song data to process' 
          }, 
          error: null 
        };
      }

      // Process each musical
      for (const musical of musicals) {
        try {
          results.processedMusicals++;
          
          // Parse the songs JSONB data
          let songsData = [];
          try {
            songsData = Array.isArray(musical?.songs) ? musical?.songs : 
                       (typeof musical?.songs === 'string' ? JSON.parse(musical?.songs) : []);
          } catch (parseError) {
            results?.errors?.push(`${musical?.title}: Failed to parse songs data - ${parseError?.message}`);
            continue;
          }

          if (!songsData?.length) {
            continue;
          }

          // Get existing songs for this musical to avoid duplicates
          const { data: existingSongs, error: existingError } = await supabase
            ?.from('songs')
            ?.select('title, musical_id')
            ?.eq('musical_id', musical?.id);

          if (existingError) {
            results?.errors?.push(`${musical?.title}: Failed to fetch existing songs - ${existingError?.message}`);
            continue;
          }

          // Create a set of existing song titles for quick lookup (case-insensitive)
          const existingSongTitles = new Set(
            (existingSongs || [])?.map(song => song?.title?.trim()?.toLowerCase())
          );

          // Process songs from JSONB data
          const songsToCreate = [];
          for (let index = 0; index < songsData?.length; index++) {
            const songData = songsData?.[index];
            
            // Extract title from various possible formats
            let songTitle = '';
            if (typeof songData === 'string') {
              songTitle = songData?.trim();
            } else if (songData?.title) {
              songTitle = songData?.title?.trim();
            } else if (songData?.name) {
              songTitle = songData?.name?.trim();
            } else if (songData?.songTitle) {
              songTitle = songData?.songTitle?.trim();
            }

            // Skip if no valid title or if song already exists
            if (!songTitle || existingSongTitles?.has(songTitle?.toLowerCase())) {
              if (!songTitle) {
                results?.errors?.push(`${musical?.title}: Song at index ${index} has no valid title`);
              } else {
                results.songsSkipped++;
              }
              continue;
            }

            // Prepare song data for creation
            songsToCreate?.push({
              musical_id: musical?.id,
              title: songTitle,
              created_by: createdBy,
              act_number: songData?.act || songData?.act_number || 1,
              song_order: songData?.order || songData?.song_order || index + 1,
              tempo: songData?.tempo || null,
              key_signature: songData?.key || songData?.key_signature || null,
              time_signature: songData?.time_signature || '4/4',
              lyrics: songData?.lyrics || null,
              audio_url: songData?.audio_url || null,
              duration_seconds: songData?.duration_seconds || null,
              is_featured: songData?.is_featured || false
            });

            // Add to existing titles to prevent duplicates within the same batch
            existingSongTitles?.add(songTitle?.toLowerCase());
          }

          // Create songs in batches if any need to be created
          if (songsToCreate?.length > 0) {
            const { data: createdSongs, error: createError } = await supabase
              ?.from('songs')
              ?.insert(songsToCreate)
              ?.select();

            if (createError) {
              results?.errors?.push(`${musical?.title}: Failed to create songs - ${createError?.message}`);
              continue;
            }

            results.songsCreated += createdSongs?.length || 0;
          }

        } catch (musicalError) {
          results?.errors?.push(`${musical?.title}: Processing error - ${musicalError?.message}`);
        }
      }

      return { 
        data: {
          ...results,
          message: `Processed ${results?.processedMusicals} musicals. Created ${results?.songsCreated} songs, skipped ${results?.songsSkipped} duplicates.`
        }, 
        error: null 
      };

    } catch (error) {
      return { 
        data: null, 
        error: error?.message || 'Failed to sync musical songs to songs table' 
      };
    }
  }

  // Sync songs from a specific musical's JSONB data to songs table
  async syncSingleMusicalSongs(musicalId, createdBy = null) {
    try {
      // Get the specific musical with its songs data
      const { data: musical, error: musicalError } = await supabase
        ?.from('musicals')
        ?.select('id, title, songs')
        ?.eq('id', musicalId)
        ?.eq('is_active', true)
        ?.single();

      if (musicalError) {
        return { data: null, error: musicalError };
      }

      if (!musical?.songs) {
        return { 
          data: { 
            songsCreated: 0, 
            songsSkipped: 0,
            message: 'No songs data found in musical' 
          }, 
          error: null 
        };
      }

      const results = {
        songsCreated: 0,
        songsSkipped: 0,
        errors: []
      };

      // Parse the songs JSONB data
      let songsData = [];
      try {
        songsData = Array.isArray(musical?.songs) ? musical?.songs : 
                   (typeof musical?.songs === 'string' ? JSON.parse(musical?.songs) : []);
      } catch (parseError) {
        return { data: null, error: `Failed to parse songs data: ${parseError?.message}` };
      }

      if (!songsData?.length) {
        return { 
          data: { 
            ...results, 
            message: 'No songs found in musical data' 
          }, 
          error: null 
        };
      }

      // Get existing songs for this musical to avoid duplicates
      const { data: existingSongs, error: existingError } = await supabase
        ?.from('songs')
        ?.select('title, musical_id')
        ?.eq('musical_id', musical?.id);

      if (existingError) {
        return { data: null, error: existingError };
      }

      // Create a set of existing song titles for quick lookup (case-insensitive)
      const existingSongTitles = new Set(
        (existingSongs || [])?.map(song => song?.title?.trim()?.toLowerCase())
      );

      // Process songs from JSONB data
      const songsToCreate = [];
      for (let index = 0; index < songsData?.length; index++) {
        const songData = songsData?.[index];
        
        // Extract title from various possible formats
        let songTitle = '';
        if (typeof songData === 'string') {
          songTitle = songData?.trim();
        } else if (songData?.title) {
          songTitle = songData?.title?.trim();
        } else if (songData?.name) {
          songTitle = songData?.name?.trim();
        } else if (songData?.songTitle) {
          songTitle = songData?.songTitle?.trim();
        }

        // Skip if no valid title or if song already exists
        if (!songTitle || existingSongTitles?.has(songTitle?.toLowerCase())) {
          if (!songTitle) {
            results?.errors?.push(`Song at index ${index} has no valid title`);
          } else {
            results.songsSkipped++;
          }
          continue;
        }

        // Prepare song data for creation
        songsToCreate?.push({
          musical_id: musical?.id,
          title: songTitle,
          created_by: createdBy,
          act_number: songData?.act || songData?.act_number || 1,
          song_order: songData?.order || songData?.song_order || index + 1,
          tempo: songData?.tempo || null,
          key_signature: songData?.key || songData?.key_signature || null,
          time_signature: songData?.time_signature || '4/4',
          lyrics: songData?.lyrics || null,
          audio_url: songData?.audio_url || null,
          duration_seconds: songData?.duration_seconds || null,
          is_featured: songData?.is_featured || false
        });

        // Add to existing titles to prevent duplicates within the same batch
        existingSongTitles?.add(songTitle?.toLowerCase());
      }

      // Create songs if any need to be created
      if (songsToCreate?.length > 0) {
        const { data: createdSongs, error: createError } = await supabase
          ?.from('songs')
          ?.insert(songsToCreate)
          ?.select();

        if (createError) {
          return { data: null, error: createError };
        }

        results.songsCreated = createdSongs?.length || 0;
      }

      return { 
        data: {
          ...results,
          message: `Created ${results?.songsCreated} songs, skipped ${results?.songsSkipped} duplicates for "${musical?.title}"`
        }, 
        error: null 
      };

    } catch (error) {
      return { 
        data: null, 
        error: error?.message || 'Failed to sync musical songs' 
      };
    }
  }

  // Get synchronization status - compare JSONB songs with songs table
  async getSyncStatus() {
    try {
      const { data: musicals, error: musicalsError } = await supabase
        ?.from('musicals')
        ?.select('id, title, songs')
        ?.eq('is_active', true)
        ?.not('songs', 'is', null);

      if (musicalsError) {
        return { data: null, error: musicalsError };
      }

      const syncStatus = {
        totalMusicals: musicals?.length || 0,
        musicalsWithSongs: 0,
        totalJsonbSongs: 0,
        totalDatabaseSongs: 0,
        musicalsNeedingSync: 0,
        details: []
      };

      for (const musical of musicals || []) {
        try {
          // Parse JSONB songs data
          let songsData = [];
          try {
            songsData = Array.isArray(musical?.songs) ? musical?.songs : 
                       (typeof musical?.songs === 'string' ? JSON.parse(musical?.songs) : []);
          } catch (parseError) {
            continue;
          }

          if (songsData?.length > 0) {
            syncStatus.musicalsWithSongs++;
            syncStatus.totalJsonbSongs += songsData?.length;

            // Get existing songs in database for this musical
            const { data: existingSongs, error: existingError } = await supabase
              ?.from('songs')
              ?.select('title')
              ?.eq('musical_id', musical?.id);

            const existingCount = existingSongs?.length || 0;
            syncStatus.totalDatabaseSongs += existingCount;

            // Count unique songs in JSONB that have valid titles
            const validJsonbSongs = songsData?.filter(song => {
              const title = typeof song === 'string' ? song : song?.title || song?.name || song?.songTitle;
              return title && title?.length > 0;
            });

            const jsonbSongTitles = new Set(
              validJsonbSongs?.map(song => {
                const title = typeof song === 'string' ? song : song?.title || song?.name || song?.songTitle;
                return title?.toLowerCase();
              })
            );

            const existingSongTitles = new Set(
              (existingSongs || [])?.map(song => song?.title?.trim()?.toLowerCase())
            );

            // Find songs that exist in JSONB but not in database
            const missingSongs = [...jsonbSongTitles]?.filter(title => 
              !existingSongTitles?.has(title)
            );

            const needsSync = missingSongs?.length > 0;
            if (needsSync) {
              syncStatus.musicalsNeedingSync++;
            }

            syncStatus?.details?.push({
              musicalId: musical?.id,
              musicalTitle: musical?.title,
              jsonbSongsCount: validJsonbSongs?.length,
              databaseSongsCount: existingCount,
              missingSongsCount: missingSongs?.length,
              needsSync
            });
          }
        } catch (err) {
          continue;
        }
      }

      return { data: syncStatus, error: null };

    } catch (error) {
      return { 
        data: null, 
        error: error?.message || 'Failed to get sync status' 
      };
    }
  }

  // Add this new function if it doesn't exist
  async getMusicalsList(filters = {}) {
    try {
      let query = supabase?.from('musicals')?.select('id, title, composer, lyricist, year_premiered, genre, is_active')?.order('title', { ascending: true });

      // Only show active musicals by default
      if (!filters?.include_inactive) {
        query = query?.eq('is_active', true);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: error?.message };
    }
  }

  // Preview duplicate removal - calls database function
  async previewDuplicateRemoval(similarityThreshold = 0.85) {
    try {
      const { data, error } = await supabase?.rpc('preview_duplicate_removal', {
        similarity_threshold: similarityThreshold
      });

      return { data: data || [], error };
    } catch (err) {
      return { data: [], error: err };
    }
  }

  // Remove duplicate musicals - calls database function
  async removeDuplicateMusicals(options = {}) {
    try {
      const { 
        similarityThreshold = 0.85, 
        dryRun = true, 
        updatedBy = null 
      } = options;

      const { data, error } = await supabase?.rpc('remove_duplicate_musicals', {
        similarity_threshold: similarityThreshold,
        dry_run: dryRun,
        updated_by_user: updatedBy
      });

      return { data: data || {}, error };
    } catch (err) {
      return { data: {}, error: err };
    }
  }

  // Add searchable musicals method for modal
  async searchMusicalsForModal(searchTerm = '') {
    try {
      let query = supabase?.from('musicals')?.select(`
          id,
          title,
          composer,
          lyricist,
          year_premiered,
          genre
        `)?.eq('is_active', true);

      if (searchTerm?.trim()) {
        query = query?.or(`title.ilike.%${searchTerm}%,composer.ilike.%${searchTerm}%,lyricist.ilike.%${searchTerm}%`);
      }

      query = query?.order('title', { ascending: true })?.limit(10);

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      return { data: data || [], error: null };
    } catch (err) {
      return { data: [], error: err };
    }
  }

  async getMusicalStatistics() {
    try {
      const { data, error } = await supabase?.from('musicals')?.select('id, genre, is_active, year_premiered');

      if (error) throw error;

      const stats = {
        total: data?.length || 0,
        active: data?.filter(m => m?.is_active)?.length || 0,
        by_genre: {},
        by_decade: {}
      };

      data?.forEach(musical => {
        // Count by genre
        if (musical?.genre) {
          stats.by_genre[musical.genre] = (stats?.by_genre?.[musical?.genre] || 0) + 1;
        }

        // Count by decade
        if (musical?.year_premiered) {
          const decade = Math.floor(musical?.year_premiered / 10) * 10;
          stats.by_decade[decade] = (stats?.by_decade?.[decade] || 0) + 1;
        }
      });

      return { data: stats, error: null };
    } catch (error) {
      return { data: null, error: error?.message };
    }
  }

  // Real-time subscription methods
  /**
   * Subscribe to real-time changes for musicals table
   * @param {Object} callbacks - Object with onInsert, onUpdate, onDelete callback functions
   * @param {Object} filter - Optional filter for subscription
   * @returns {Function} Unsubscribe function
   */
  subscribeToMusicals(callbacks = {}, filter = null) {
    const { onInsert, onUpdate, onDelete } = callbacks;
    
    const channelConfig = {
      event: '*',
      schema: 'public',
      table: 'musicals'
    };

    if (filter) {
      channelConfig.filter = `${filter?.column}=eq.${filter?.value}`;
    }

    const channel = supabase?.channel('musicals-changes')?.on('postgres_changes', channelConfig, (payload) => {
        const { eventType, new: newRecord, old: oldRecord } = payload;
        
        switch (eventType) {
          case 'INSERT':
            onInsert?.(newRecord);
            break;
          case 'UPDATE':
            onUpdate?.(newRecord, oldRecord);
            break;
          case 'DELETE':
            onDelete?.(oldRecord);
            break;
        }
      })?.subscribe();

    // Return unsubscribe function
    return () => {
      supabase?.removeChannel(channel);
    };
  }

  /**
   * Subscribe to real-time changes for active musicals only
   * @param {Object} callbacks - Object with onInsert, onUpdate, onDelete callback functions
   * @returns {Function} Unsubscribe function
   */
  subscribeToActiveMusicals(callbacks = {}) {
    return this.subscribeToMusicals(callbacks, {
      column: 'is_active',
      value: true
    });
  }
}

export default new MusicalService();

// Search musicals with fuzzy matching
export const searchMusicals = async (searchTerm, options = {}) => {
  try {
    if (!searchTerm?.trim()) {
      return getMusicals(options);
    }

    let query = supabase?.from('musicals')?.select(`
        *,
        roles:roles(
          id,
          character_name,
          role_type,
          voice_type,
          description
        ),
        songs:songs(
          id,
          title,
          act_number,
          song_order
        )
      `)?.or(`
        title.ilike.%${searchTerm}%,composer.ilike.%${searchTerm}%,lyricist.ilike.%${searchTerm}%,book_writer.ilike.%${searchTerm}%
      `);

    if (options?.active_only !== false) {
      query = query?.eq('is_active', true);
    }

    if (options?.genre) {
      query = query?.eq('genre', options?.genre);
    }

    if (options?.year_from) {
      query = query?.gte('year_premiered', options?.year_from);
    }

    if (options?.year_to) {
      query = query?.lte('year_premiered', options?.year_to);
    }

    query = query?.order('title', { ascending: true });

    const { data, error } = await query;

    if (error) throw error;
    return { data: data || [], error: null };
  } catch (error) {
    return { data: [], error: error?.message };
  }
};

export const musicalService = new MusicalService();